import { useState } from 'react';
import api from '../services/api';

export default function BookingForm({ events = [], onSuccess, onCancel, preFilledArtistId, preFilledArtistName }) {
  const [formData, setFormData] = useState({
    eventId: '',
    artistId: preFilledArtistId || '',
    message: '',
    proposedFee: '',
    currency: 'EUR',
    technicalRequirements: '',
    travelIncluded: false,
    accommodationIncluded: false,
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Recherche d'artistes
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(
    preFilledArtistId ? { id: preFilledArtistId, artistName: preFilledArtistName } : null
  );

  function validateForm() {
    const newErrors = {};

    if (!formData.eventId) {
      newErrors.eventId = 'Sélectionnez un événement';
    }

    if (!formData.artistId) {
      newErrors.artistId = 'Sélectionnez un artiste';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSearchArtists() {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/artists?search=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data.artists || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  function handleSelectArtist(artist) {
    setSelectedArtist(artist);
    setFormData((prev) => ({ ...prev, artistId: artist.id }));
    setSearchResults([]);
    setSearchQuery('');
    if (errors.artistId) {
      setErrors((prev) => ({ ...prev, artistId: null }));
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        eventId: formData.eventId,
        artistId: formData.artistId,
      };
      if (formData.message.trim()) {
        payload.message = formData.message.trim();
      }
      if (formData.proposedFee) {
        payload.proposedFee = Math.round(parseFloat(formData.proposedFee) * 100);
        payload.currency = formData.currency;
      }
      if (formData.technicalRequirements.trim()) {
        payload.technicalRequirements = formData.technicalRequirements.trim();
      }
      if (formData.travelIncluded) {
        payload.travelIncluded = true;
      }
      if (formData.accommodationIncluded) {
        payload.accommodationIncluded = true;
      }
      if (formData.notes.trim()) {
        payload.notes = formData.notes.trim();
      }

      await api.post('/bookings', payload);
      onSuccess?.();
    } catch (err) {
      setApiError(err.message || 'Erreur lors de la création du booking');
    } finally {
      setLoading(false);
    }
  }

  // Filtrer les événements publiés
  const publishedEvents = events.filter((e) => e.status === 'published');

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {apiError && (
        <div className="border border-status-rejected/30 text-status-rejected px-3 py-2 text-sm font-mono">
          {apiError}
        </div>
      )}

      {/* Sélection de l'événement */}
      <div>
        <label htmlFor="bookingEvent" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Événement <span className="text-accent">*</span>
        </label>
        {publishedEvents.length === 0 ? (
          <p className="th-text-muted text-sm">Aucun événement publié. Publiez un événement d'abord.</p>
        ) : (
          <select
            id="bookingEvent"
            name="eventId"
            value={formData.eventId}
            onChange={handleChange}
            className="bg-transparent border th-border th-text text-sm focus:border-accent outline-none transition-colors duration-150 px-3 py-2.5 w-full"
          >
            <option value="">Choisir un événement</option>
            {publishedEvents.map((event) => (
              <option key={event.id} value={event.id}>
                {event.title} · {event.venueName || event.location?.city || 'Pas de lieu'}
              </option>
            ))}
          </select>
        )}
        {errors.eventId && <p className="text-status-rejected text-xs mt-1 font-mono">{errors.eventId}</p>}
      </div>

      {/* Recherche d'artiste */}
      <div>
        <label className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Artiste <span className="text-accent">*</span>
        </label>
        {selectedArtist ? (
          <div className="flex items-center justify-between bg-transparent border border-accent bg-accent/10 px-3 py-2.5">
            <span className="text-sm th-text">{selectedArtist.artistName}</span>
            <button
              type="button"
              onClick={() => {
                setSelectedArtist(null);
                setFormData((prev) => ({ ...prev, artistId: '' }));
              }}
              className="text-xs th-text-muted hover:text-status-rejected transition-colors duration-150"
            >
              Changer
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchArtists();
                  }
                }}
                placeholder="Rechercher un artiste par nom..."
                className="th-input flex-1"
              />
              <button
                type="button"
                onClick={handleSearchArtists}
                disabled={searching}
                className="th-btn disabled:opacity-50"
              >
                {searching ? '...' : 'Chercher'}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 space-y-px dark:bg-dark-border bg-light-border max-h-48 overflow-y-auto">
                {searchResults.map((artist) => (
                  <button
                    type="button"
                    key={artist.id}
                    onClick={() => handleSelectArtist(artist)}
                    className="w-full th-bg p-3 flex items-center justify-between hover:opacity-80 transition-opacity duration-150 text-left"
                  >
                    <div>
                      <p className="text-sm font-mono font-medium th-text">{artist.artistName}</p>
                      <p className="text-xs th-text-muted font-mono">{artist.city || 'Pas de localisation'}</p>
                    </div>
                    <span className="text-xs text-accent font-mono">Sélectionner</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {errors.artistId && <p className="text-status-rejected text-xs mt-1 font-mono">{errors.artistId}</p>}
      </div>

      {/* Message */}
      <div>
        <label htmlFor="bookingMessage" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Message
        </label>
        <textarea
          id="bookingMessage"
          name="message"
          value={formData.message}
          onChange={handleChange}
          maxLength={1000}
          rows={3}
          placeholder="Message pour l'artiste (optionnel)"
          className="th-input resize-none"
        />
      </div>

      {/* Cachet proposé */}
      <div className="grid grid-cols-[2fr_1fr] gap-3">
        <div>
          <label htmlFor="proposedFee" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
            Cachet proposé
          </label>
          <input
            id="proposedFee"
            type="number"
            name="proposedFee"
            value={formData.proposedFee}
            onChange={handleChange}
            min="0"
            step="1"
            placeholder="Montant en euros"
            className="th-input w-full"
          />
        </div>
        <div>
          <label htmlFor="currency" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
            Devise
          </label>
          <select
            id="currency"
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="bg-transparent border th-border th-text text-sm focus:border-accent outline-none transition-colors duration-150 px-3 py-2.5 w-full"
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
      </div>

      {/* Rider technique */}
      <div>
        <label htmlFor="technicalRequirements" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Rider technique
        </label>
        <textarea
          id="technicalRequirements"
          name="technicalRequirements"
          value={formData.technicalRequirements}
          onChange={handleChange}
          maxLength={5000}
          rows={3}
          placeholder="Exigences techniques (optionnel)"
          className="th-input resize-none"
        />
        <p className="text-xs th-text-muted mt-1">{formData.technicalRequirements.length} / 5000</p>
      </div>

      {/* Conditions de voyage */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="travelIncluded"
            checked={formData.travelIncluded}
            onChange={handleChange}
            className="w-4 h-4"
          />
          <span className="text-xs font-mono th-text-secondary uppercase tracking-widest">Voyage inclus</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="accommodationIncluded"
            checked={formData.accommodationIncluded}
            onChange={handleChange}
            className="w-4 h-4"
          />
          <span className="text-xs font-mono th-text-secondary uppercase tracking-widest">Hébergement inclus</span>
        </label>
      </div>

      {/* Notes internes */}
      <div>
        <label htmlFor="notes" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Notes internes <span className="text-accent text-[10px]">(visible uniquement par vous)</span>
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          maxLength={2000}
          rows={2}
          placeholder="Notes internes (optionnel)"
          className="th-input resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={loading || publishedEvents.length === 0}
          className="flex-1 th-btn-accent disabled:opacity-50"
        >
          {loading ? 'Envoi...' : 'Envoyer l\'invitation'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 th-btn"
          >
            Annuler
          </button>
        )}
      </div>
    </form>
  );
}
